import { useCallback, useEffect, useRef, useState } from "react";
import * as Y from "yjs";
import Quill from "quill";
import QuillCursors from "quill-cursors";
import { QuillBinding } from "y-quill";
import "quill/dist/quill.snow.css";

/*
  Der Editor — Werkzeugleiste, Gliederung, Blatt.

  ---------------------------------------------------------------
  Warum Quill und nicht mehr selbst gebaut
  ---------------------------------------------------------------

  Der erste Anlauf war ein Eigenbau: je Absatz ein Textfeld, gebunden an
  einen `Y.Text`. Das lief — aber es konnte keine Auszeichnungen im Satz,
  kein echtes Rückgängig und keine richtigen Listen, und es sah aus wie ein
  Formular.

  Fett und Kursiv nachzurüsten geht nicht obendrauf: Eine Auszeichnung, die
  zwei Leute gleichzeitig auf überlappende Stellen setzen, muss
  zusammengeführt werden wie der Text selbst. Das gehört ins Fundament.

  ---------------------------------------------------------------
  Warum die Werkzeugleiste NICHT im Blatt steht
  ---------------------------------------------------------------

  Quill setzt sie von Haus aus über den Text, also ins Blatt hinein. So
  sieht es kein Textprogramm, das jemand kennt: Dort läuft die Leiste über
  die ganze Breite des Fensters, und das Blatt liegt darunter auf dem Tisch.
  Deshalb wird der Leiste hier ein eigener Platz gegeben und Quill nur
  gesagt, wo sie steht.

  ---------------------------------------------------------------
  Was bewusst NICHT in der Leiste steht
  ---------------------------------------------------------------

  Keine Schrift- und keine Hintergrundfarbe. Rot, Schwarz, Grün, Blau, Gelb
  und Magenta sind in der deutschen Politik Parteifarben, und die Software
  wird von allen Fraktionen eines Rates benutzt. Ein Antrag, in dem jemand
  eine Überschrift rot einfärbt, ist ein anderes Dokument als einer, in dem
  sie schwarz ist.

  Und kein Lineal. Bei Google steht dort eines, aber es kann etwas — Ränder
  und Tabulatoren ziehen. Eines, das nur so aussieht, verstößt gegen die
  Regel des Steckbriefs: Was fertig aussieht und beim Anfassen nichts tut,
  zerstört mehr Vertrauen als etwas, das ehrlich fehlt.
*/

Quill.register("modules/cursors", QuillCursors);

/*
  Die Farben der fremden Schreibmarken.

  Es sind die Farben des Textmarkers — im Steckbrief ausdrücklich als
  unpolitisches Zeichensystem begründet. Neben jeder Marke steht der Name;
  die Farbe beschleunigt das Wiedererkennen, sie trägt es nicht.
*/
const MARKENFARBEN = ["#c8a415", "#3f8f5a", "#2f6f9f", "#b0517e", "#c2732a"];

export function farbeFuer(kontoId) {
  let summe = 0;
  for (let i = 0; i < String(kontoId).length; i += 1) {
    summe = (summe + String(kontoId).charCodeAt(i)) % 997;
  }
  return MARKENFARBEN[summe % MARKENFARBEN.length];
}

/* Zoomstufen. Für eine Zielgruppe, die teilweise über siebzig ist, kein Beiwerk. */
const STUFEN = [75, 90, 100, 125, 150, 200];

export default function Textwerk({
  doc,
  praesenz = [],
  aufAuswahl,
  aufBereit,
  /* Die Kommentarspalte. Sie gehört ins selbe Raster wie Gliederung und Blatt. */
  rand = null,
  nurLesen = false,
}) {
  const behaelterRef = useRef(null);
  const leisteRef = useRef(null);
  const quillRef = useRef(null);
  const bindungRef = useRef(null);
  const rueckgaengigRef = useRef(null);

  /*
    Kopf- und Fußzeile sind eigene kleine Editoren im selben Dokument —
    eigene Texte in demselben Yjs-Dokument. Damit gilt für sie alles, was
    für den Fließtext gilt: gleichzeitiges Schreiben, Rückgängig, und beim
    Anlegen aus einem Muster kommen sie mit.
  */
  const kopfRef = useRef(null);
  const kopfLeisteRef = useRef(null);
  const fussRef = useRef(null);
  const fussLeisteRef = useRef(null);
  const randBindungen = useRef([]);

  const [zoom, setzeZoom] = useState(100);
  const [gliederung, setzeGliederung] = useState([]);
  const [kannZurueck, setzeKannZurueck] = useState(false);
  const [kannVor, setzeKannVor] = useState(false);

  const aufAuswahlRef = useRef(aufAuswahl);
  aufAuswahlRef.current = aufAuswahl;
  const aufBereitRef = useRef(aufBereit);
  aufBereitRef.current = aufBereit;

  /* ---------- Editor aufbauen ---------- */

  useEffect(() => {
    if (!behaelterRef.current || !leisteRef.current || quillRef.current) return undefined;

    const quill = new Quill(behaelterRef.current, {
      theme: "snow",
      readOnly: nurLesen,
      placeholder: "Hier steht der Antrag …",
      modules: {
        /* Die Leiste steht außerhalb des Blattes — Quill bekommt nur gesagt, wo. */
        toolbar: nurLesen ? false : { container: leisteRef.current },
        cursors: { transformOnTextChange: true },
        /*
          Quills eigenes Rückgängig bleibt AUS. Es kennt nur seinen eigenen
          Stapel und weiß nichts von den anderen — es nähme beim Zurücknehmen
          fremde Änderungen mit. Stattdessen der Rückgängig-Verwalter von
          Yjs, gleich darunter.
        */
        history: { delay: 0, maxStack: 0, userOnly: true },
      },
    });

    quillRef.current = quill;

    const ytext = doc.getText("quill");
    bindungRef.current = new QuillBinding(ytext, quill);

    /*
      Kopf und Fuß. Ohne Schreibmarken der anderen: Deren Stelle wird als
      Zahl im Fließtext gemeldet: Dieselbe Zahl in der Kopfzeile zu zeichnen
      hieße, eine Marke an eine völlig andere Stelle zu setzen.
    */
    const kopfZeilen = [];
    if (kopfRef.current && kopfLeisteRef.current) {
      const kopf = new Quill(kopfRef.current, {
        theme: "snow",
        readOnly: nurLesen,
        placeholder: "Kopfzeile — Fraktionsname, Anschrift",
        modules: {
          toolbar: nurLesen ? false : { container: kopfLeisteRef.current },
          history: { delay: 0, maxStack: 0, userOnly: true },
        },
      });
      kopfZeilen.push([kopf, doc.getText("kopf")]);
      benenneWerkzeuge(kopfLeisteRef.current);
    }
    if (fussRef.current && fussLeisteRef.current) {
      const fuss = new Quill(fussRef.current, {
        theme: "snow",
        readOnly: nurLesen,
        placeholder: "Fußzeile — Bankverbindung, Sprecher, Datum",
        modules: {
          toolbar: nurLesen ? false : { container: fussLeisteRef.current },
          history: { delay: 0, maxStack: 0, userOnly: true },
        },
      });
      kopfZeilen.push([fuss, doc.getText("fuss")]);
      benenneWerkzeuge(fussLeisteRef.current);
    }

    randBindungen.current = kopfZeilen.map(([q, y]) => new QuillBinding(y, q));

    /*
      Rückgängig über Yjs.

      Wichtig ist `trackedOrigins`: Zurückgenommen wird ausschließlich, was
      aus DIESEM Browser kam. Wer Strg+Z drückt, während der andere gerade
      schreibt, nimmt seine eigene letzte Änderung zurück — nicht dessen
      Satz. Genau das kann Quills eingebautes Rückgängig nicht.
    */
    /*
      Rückgängig umfasst alle drei Texte. Wer eine Zeile im Briefkopf
      löscht und Strg+Z drückt, erwartet sie zurück — nicht die letzte
      Änderung im Fließtext.
    */
    const zurueck = new Y.UndoManager([ytext, doc.getText("kopf"), doc.getText("fuss")], {
      trackedOrigins: new Set([bindungRef.current, ...randBindungen.current, null]),
      captureTimeout: 400,
    });
    rueckgaengigRef.current = zurueck;

    const standMelden = () => {
      setzeKannZurueck(zurueck.canUndo());
      setzeKannVor(zurueck.canRedo());
    };
    zurueck.on("stack-item-added", standMelden);
    zurueck.on("stack-item-popped", standMelden);
    zurueck.on("stack-cleared", standMelden);

    /* Die gewohnten Tastenkürzel. Ohne die sucht niemand einen Knopf. */
    const beiTaste = (e) => {
      const befehl = e.metaKey || e.ctrlKey;
      if (!befehl || e.key.toLowerCase() !== "z") return;
      e.preventDefault();
      if (e.shiftKey) zurueck.redo();
      else zurueck.undo();
    };
    quill.root.addEventListener("keydown", beiTaste);

    /* ---------- Gliederung ---------- */

    const gliederungBauen = () => {
      const zeilen = quill.getLines();
      const gefunden = [];
      for (const zeile of zeilen) {
        const stufe = zeile.domNode && /^H([123])$/.exec(zeile.domNode.tagName);
        if (!stufe) continue;
        const text = zeile.domNode.textContent.trim();
        if (!text) continue;
        gefunden.push({ stufe: Number(stufe[1]), text, stelle: quill.getIndex(zeile) });
      }
      setzeGliederung(gefunden);
    };

    gliederungBauen();
    quill.on("text-change", gliederungBauen);

    quill.on("selection-change", (bereich) => {
      if (!aufAuswahlRef.current) return;
      if (!bereich) {
        aufAuswahlRef.current(null);
        return;
      }
      aufAuswahlRef.current({
        von: bereich.index,
        bis: bereich.index + bereich.length,
        zitat: bereich.length > 0 ? quill.getText(bereich.index, bereich.length) : "",
        stelle: kastenFuer(quill, bereich),
      });
    });

    benenneWerkzeuge(leisteRef.current);
    if (aufBereitRef.current) aufBereitRef.current(quill);

    return () => {
      quill.root.removeEventListener("keydown", beiTaste);
      quill.off("text-change", gliederungBauen);
      zurueck.destroy();
      rueckgaengigRef.current = null;
      if (bindungRef.current) bindungRef.current.destroy();
      bindungRef.current = null;
      randBindungen.current.forEach((b) => b.destroy());
      randBindungen.current = [];
      quillRef.current = null;
    };
  }, [doc, nurLesen]);

  /* ---------- Die Schreibmarken der anderen ---------- */

  const zeichneMarken = useCallback(() => {
    const quill = quillRef.current;
    if (!quill) return;
    const marken = quill.getModule("cursors");
    if (!marken) return;

    const da = new Set();
    for (const person of praesenz) {
      if (!Number.isFinite(person.von)) continue;
      da.add(person.kontoId);
      marken.createCursor(person.kontoId, person.name, person.farbe || farbeFuer(person.kontoId));
      marken.moveCursor(person.kontoId, {
        index: person.von,
        length: Math.max(0, (person.bis ?? person.von) - person.von),
      });
    }

    for (const vorhanden of marken.cursors()) {
      if (!da.has(vorhanden.id)) marken.removeCursor(vorhanden.id);
    }
  }, [praesenz]);

  useEffect(() => {
    zeichneMarken();
  }, [zeichneMarken]);

  function springeZuUeberschrift(stelle) {
    const quill = quillRef.current;
    if (!quill) return;
    quill.setSelection(stelle, 0, "user");
    quill.focus();
  }

  return (
    <div className="textbuero">
      {/* ---------- Werkzeugleiste über die ganze Breite ---------- */}

      <div className="werkzeugleiste nicht-drucken">
        {!nurLesen && (
          <div className="werkzeugleiste__eigen">
            <button
              className="wkz"
              title="Rückgängig (Strg+Z)"
              aria-label="Rückgängig"
              disabled={!kannZurueck}
              onClick={() => rueckgaengigRef.current && rueckgaengigRef.current.undo()}
            >
              <Pfeil richtung="zurueck" />
            </button>
            <button
              className="wkz"
              title="Wiederholen (Strg+Umschalt+Z)"
              aria-label="Wiederholen"
              disabled={!kannVor}
              onClick={() => rueckgaengigRef.current && rueckgaengigRef.current.redo()}
            >
              <Pfeil richtung="vor" />
            </button>

            <span className="werkzeugleiste__trenner" />

            <label className="wkz-zoom">
              <span className="nur-fuer-vorleser">Vergrößerung</span>
              <select value={zoom} onChange={(e) => setzeZoom(Number(e.target.value))}>
                {STUFEN.map((s) => (
                  <option key={s} value={s}>
                    {s} %
                  </option>
                ))}
              </select>
            </label>

            <span className="werkzeugleiste__trenner" />
          </div>
        )}

        {/*
          Das hier füllt Quill selbst mit Leben: Es erkennt die Knöpfe an
          ihren Klassen und hängt seine Befehle daran. Geschrieben ist es
          von Hand, damit die Reihenfolge stimmt und alles deutsch ist.
        */}
        <div className="werkzeugleiste__quill" ref={leisteRef}>
          <span className="ql-formats">
            <select className="ql-header" defaultValue="">
              <option value="1" />
              <option value="2" />
              <option value="3" />
              <option value="" />
            </select>
            <select className="ql-size" defaultValue="">
              <option value="small" />
              <option value="" />
              <option value="large" />
              <option value="huge" />
            </select>
          </span>

          <span className="ql-formats">
            <button className="ql-bold" />
            <button className="ql-italic" />
            <button className="ql-underline" />
          </span>

          <span className="ql-formats">
            <button className="ql-list" value="ordered" />
            <button className="ql-list" value="bullet" />
            <button className="ql-indent" value="-1" />
            <button className="ql-indent" value="+1" />
          </span>

          <span className="ql-formats">
            <select className="ql-align" defaultValue="">
              <option value="" />
              <option value="center" />
              <option value="right" />
              <option value="justify" />
            </select>
          </span>

          <span className="ql-formats">
            <button className="ql-blockquote" />
            <button className="ql-link" />
            <button className="ql-clean" />
          </span>
        </div>
      </div>

      {/* ---------- Gliederung, Blatt ---------- */}

      <div className="textbuero__flaeche">
        <aside className="gliederung nicht-drucken">
          <p className="gliederung__kopf">Gliederung</p>
          {gliederung.length === 0 ? (
            <p className="klein leise">
              Überschriften, die du im Text setzt, erscheinen hier — und führen mit einem Tipp
              dorthin.
            </p>
          ) : (
            <ul className="gliederung__liste">
              {gliederung.map((eintrag, i) => (
                <li key={`${eintrag.stelle}-${i}`}>
                  <button
                    className={`gliederung__punkt gliederung__punkt--${eintrag.stufe}`}
                    onClick={() => springeZuUeberschrift(eintrag.stelle)}
                  >
                    {eintrag.text}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <div className="blatt">
          {/*
            `zoom` und nicht `transform`: Beim Skalieren über `transform`
            bliebe der Platzbedarf gleich, das Blatt ragte aus seinem Kasten
            heraus oder ließe eine Lücke. `zoom` rechnet das Layout wirklich
            neu — auch die Schreibmarken der anderen sitzen dann richtig.
          */}
          <div className="blatt__seite" style={{ zoom: `${zoom}%` }}>
            {/*
              Kopf und Fuß stehen EINMAL oben und unten, nicht auf jeder
              Seite — auf dem Bildschirm gibt es keine Seiten, das Dokument
              ist eine durchgehende Bahn. Wie auf einem Briefbogen also.
              Beim Drucken wiederholen sie sich; das macht das Druckblatt
              im Stilblatt.
            */}
            <div className="randzeile randzeile--kopf">
              <div className="randzeile__marke nicht-drucken">Kopfzeile</div>
              <div className="randzeile__leiste nicht-drucken" ref={kopfLeisteRef}>
                <RandWerkzeuge />
              </div>
              <div ref={kopfRef} />
            </div>

            <div ref={behaelterRef} />

            <div className="randzeile randzeile--fuss">
              <div className="randzeile__marke nicht-drucken">Fußzeile</div>
              <div className="randzeile__leiste nicht-drucken" ref={fussLeisteRef}>
                <RandWerkzeuge />
              </div>
              <div ref={fussRef} />
            </div>
          </div>
        </div>

        {rand}
      </div>
    </div>
  );
}

/*
  Die schmale Leiste für Kopf- und Fußzeile.

  Nur das, was ein Briefkopf braucht: Fettung für den Fraktionsnamen,
  Kursiv, und die Ausrichtung — Anschrift links, Datum rechts. Keine
  Überschriften, keine Listen: In einer Kopfzeile haben sie nichts zu
  suchen, und jede Möglichkeit mehr ist eine Möglichkeit, ein Briefpapier
  unbrauchbar zu machen.
*/
function RandWerkzeuge() {
  return (
    <>
      <span className="ql-formats">
        <button className="ql-bold" />
        <button className="ql-italic" />
      </span>
      <span className="ql-formats">
        <select className="ql-align" defaultValue="">
          <option value="" />
          <option value="center" />
          <option value="right" />
        </select>
      </span>
    </>
  );
}

/* Die beiden Pfeile für Rückgängig und Wiederholen. */
export function Pfeil({ richtung }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <path
        d="M9 14 4 9l5-5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        transform={richtung === "vor" ? "scale(-1,1) translate(-24,0)" : undefined}
      />
      <path
        d="M4 9h9a6 6 0 0 1 0 12h-3"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        transform={richtung === "vor" ? "scale(-1,1) translate(-24,0)" : undefined}
      />
    </svg>
  );
}

/*
  Wo steht die Auswahl auf dem Bildschirm?

  Quill kennt die Stelle im Text, nicht die im Fenster. Gebraucht wird das,
  damit das Notizfeld dort aufgeht, wo eben noch der Finger war.
*/
function kastenFuer(quill, bereich) {
  try {
    const kasten = quill.getBounds(bereich.index, bereich.length);
    const wurzel = quill.root.getBoundingClientRect();
    return {
      /* Für den Knopf, der aus der Auswahl eine Notiz macht: ein Punkt. */
      x: wurzel.left + kasten.left + kasten.width / 2,
      y: wurzel.top + kasten.top,
      /*
        Für den Knopf: INNEN am rechten Blattrand, im weißen Seitenrand.

        Außerhalb des Blattes stieß er mit der Kommentarspalte zusammen und
        verdeckte deren Knöpfe. Im Seitenrand ist Platz, der ohnehin leer
        ist — dort steht er niemandem im Weg und ist trotzdem neben der
        Stelle, um die es geht.
      */
      randX: wurzel.right - 52,
      /*
        Für das Notizfeld: das ganze RECHTECK der Auswahl.

        ---------------------------------------------------------------
        Warum hier zwei Formen nebeneinander stehen
        ---------------------------------------------------------------

        Das Notizfeld muss entscheiden, ob es unter die Stelle passt oder
        darüber gehört — dafür reicht ein Punkt nicht, es braucht Oberkante
        und Unterkante. `platziere` in `Notizfeld.js` liest deshalb
        `links`, `oben` und `unten`, so wie `textauswahl.js` es für die
        Standbild-Dokumente liefert.

        Bis hierher gab es die drei Felder nur dort. Der Textdokument-Weg
        gab `x` und `y`, `platziere` fand seine Felder nicht und rechnete
        mit `undefined` weiter: Heraus kam `left: "undefinedpx"`, was der
        Browser stillschweigend verwirft. Das Feld war damit gar nicht
        platziert und landete dort, wo der Textfluss es zufällig ablegte —
        bei einem langen Antrag weit unterhalb des Bildschirms. Und weil es
        `position: fixed` hat, scrollte es auch nicht mit: Ein Kommentar
        ließ sich schlicht nicht schreiben.

        Gemessen am 2. August 2026: Fenster 1280 × 720, Feld bei 1554 Pixel.
      */
      links: wurzel.left + kasten.left,
      oben: wurzel.top + kasten.top,
      unten: wurzel.top + kasten.bottom,
    };
  } catch {
    /*
      Quill kennt die Stelle nicht mehr — dann lieber grob die Mitte als
      gar nichts. Die drei Rechteckfelder müssen auch hier stimmen, sonst
      wäre genau der Fehler von oben wieder da.
    */
    const mitteX = window.innerWidth / 2;
    const mitteY = window.innerHeight / 2;
    return {
      x: mitteX,
      y: mitteY,
      randX: window.innerWidth - 120,
      links: mitteX - 160,
      oben: mitteY,
      unten: mitteY,
    };
  }
}

/* Deutsche Titel für die Knöpfe. */
const TITEL = {
  "ql-bold": "Fett (Strg+B)",
  "ql-italic": "Kursiv (Strg+I)",
  "ql-underline": "Unterstrichen (Strg+U)",
  "ql-blockquote": "Als Zitat einrücken",
  "ql-link": "Verweis einfügen",
  "ql-clean": "Auszeichnungen entfernen",
};

function benenneWerkzeuge(leiste) {
  if (!leiste) return;

  for (const [klasse, titel] of Object.entries(TITEL)) {
    const knopf = leiste.querySelector(`button.${klasse}`);
    if (!knopf) continue;
    knopf.setAttribute("title", titel);
    knopf.setAttribute("aria-label", titel);
  }

  /* Listen und Einzüge unterscheiden sich nur im Wert, nicht in der Klasse. */
  const paare = [
    ['button.ql-list[value="ordered"]', "Nummerierte Liste"],
    ['button.ql-list[value="bullet"]', "Aufzählung"],
    ['button.ql-indent[value="+1"]', "Weiter einrücken"],
    ['button.ql-indent[value="-1"]', "Weniger einrücken"],
  ];
  for (const [wahl, titel] of paare) {
    const knopf = leiste.querySelector(wahl);
    if (!knopf) continue;
    knopf.setAttribute("title", titel);
    knopf.setAttribute("aria-label", titel);
  }

  const kopf = leiste.querySelector(".ql-header .ql-picker-label");
  if (kopf) kopf.setAttribute("aria-label", "Art des Absatzes");
  const groesse = leiste.querySelector(".ql-size .ql-picker-label");
  if (groesse) groesse.setAttribute("aria-label", "Schriftgröße");
  const ausrichtung = leiste.querySelector(".ql-align .ql-picker-label");
  if (ausrichtung) ausrichtung.setAttribute("aria-label", "Ausrichtung");
}
